import { useEffect, useState, useMemo } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import {
  GOLD, ACCENT_GOLD, RADIUS, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  TEXTURED_PANEL_STYLE, INPUT_STYLE, FourCorners, OrnamentalDivider, VIGNETTE,
} from '../../lib/medievalTheme';
import { CloseButton } from '../ui/navButtons';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { k, t } from '../../localization/t';

const TARGET_KIND_LABEL = {
  Avatar: k('Avatar'),
  Minion: k('Minion'),
  Site: k('Site'),
};

/**
 * Centered modal for choosing the equipment target. Replaces the
 * earlier flyout submenu so the user gets a real searchable picker
 * with proper card art instead of a 16cqw context-menu strip.
 *
 * Targets are passed in pre-filtered for legality by the source card's
 * type. See GameBoard.getValidEquipTargets (Auras can target Minion /
 * Avatar / Site, Artifacts only Minion / Avatar).
 */
export default function EquipTargetModal({ source, targets, onEquip, onClose }) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    const id = pushDismiss(() => onClose());
    return () => removeDismiss(id);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [targets, query]);

  return createPortal(
    <motion.div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1200] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
    >
      <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
      <motion.div
        className="relative flex flex-col p-5"
        style={{
          ...TEXTURED_PANEL_STYLE,
          width: '52cqw',
          maxWidth: '90cqw',
          maxHeight: '80cqh',
          boxShadow: `${TEXTURED_PANEL_STYLE.boxShadow}, 0 0 28px rgba(212,168,67,0.22)`,
        }}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.14, ease: [0.2, 0.7, 0.3, 1] }}
      >
        <FourCorners />

        <div className="flex items-start gap-4 pr-10">
          <div className="flex-1 min-w-0">
            <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
              {t('Equip')}
            </div>
            <h2
              className="text-lg font-bold arena-heading truncate"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
              title={source?.name}
            >
              {source?.name || t('Card')}
            </h2>
            <div className="text-xs" style={{ color: TEXT_MUTED }}>
              {t('Choose a target to attach this card to.')}
            </div>
          </div>
          <div className="absolute top-3 right-3">
            <CloseButton onClick={onClose} />
          </div>
        </div>

        <OrnamentalDivider className="my-3 shrink-0" />

        <div className="relative shrink-0">
          <Search
            style={{
              width: '0.9cqw',
              height: '0.9cqw',
              color: TEXT_MUTED,
              position: 'absolute',
              left: '0.7cqw',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <input
            autoFocus
            type="search"
            placeholder={t('Search targets by name...')}
            value={query}
            onInput={(e) => setQuery(e.target.value)}
            className="w-full py-2 text-sm outline-none"
            style={{
              ...INPUT_STYLE,
              paddingLeft: '2.2cqw',
              paddingRight: '0.9cqw',
              borderRadius: 'var(--radius-md)',
              color: TEXT_PRIMARY,
            }}
          />
        </div>

        <div className="mt-2 flex items-center justify-between text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
          <span>
            {targets.length === 1 ? t('{n} of 1 target', { n: filtered.length }) : t('{n} of {total} targets', { n: filtered.length, total: targets.length })}
          </span>
          <span>{t('Click a card to attach')}</span>
        </div>

        {/* The scroll viewport intentionally has vertical AND horizontal
            padding so the hover-lift animation on TargetCard (translate
            + scale + outer glow) has room to render fully without
            running into the scroll container's clip edge. */}
        <div
          className="mt-3 flex-1 min-h-0 overflow-y-auto"
          style={{ padding: '0.75cqw 0.75cqw 0.75cqw 0.25cqw' }}
        >
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm" style={{ color: TEXT_MUTED }}>
              {targets.length === 0
                ? t('You control no other cards to attach this to.')
                : t('No targets match your search.')}
            </div>
          ) : (
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11cqw, 1fr))' }}
            >
              {filtered.map((t) => (
                <TargetCard key={t.id} target={t} onEquip={onEquip} />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.querySelector('.app-canvas') ?? document.body,
  );
}

function TargetCard({ target, onEquip }) {
  const [hovered, setHovered] = useState(false);
  const isSite = !!target.isSite;
  const kind = TARGET_KIND_LABEL[target.type] || (isSite ? k('Site') : k('Card'));

  return (
    <button
      type="button"
      onClick={() => onEquip(target)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-center gap-1.5 cursor-pointer text-left"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        outline: 'none',
      }}
    >
      {/* Rounded corners live on the <img> itself rather than on a
          clipping wrapper. Keeping the frame overflow visible means
          the hover lift / scale / outer glow render outside the
          natural card bounds without being cut off. */}
      <div
        style={{
          width: '100%',
          aspectRatio: isSite ? '88.9 / 63.5' : '63.5 / 88.9',
          borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
          background: 'rgba(20,16,12,0.7)',
          border: `1px solid ${GOLD} ${hovered ? 0.85 : 0.45})`,
          boxShadow: hovered
            ? `0 10px 24px rgba(0,0,0,0.65), 0 0 18px rgba(212,168,67,0.45)`
            : '0 6px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
          transform: hovered ? 'translateY(-3px) scale(1.02)' : 'translateY(0) scale(1)',
          transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        }}
      >
        {target.imageUrl ? (
          <img
            src={target.imageUrl}
            alt={target.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
            }}
          />
        ) : null}
      </div>
      <div className="w-full text-center px-1">
        <div
          className="text-xs font-semibold truncate"
          style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
          title={target.name}
        >
          {target.name}
        </div>
        <div className="text-[0.625cqw] mt-0.5" style={{ color: hovered ? ACCENT_GOLD : TEXT_MUTED }}>
          {t(kind)}
          {target.equippedCount > 0 ? t(' · {n} equipped', { n: target.equippedCount }) : ''}
        </div>
      </div>
    </button>
  );
}
